import { compact } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useEffect, useState, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AgentStatusBadge } from 'app/components/ai-agent-settings/AiAgentStatusCells'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { TONE_WORDS, agentStatusLines, type AgentStatusTone } from 'app/lib/aiAgentOverview'
import { useAIAgentStore, useLocationStore } from 'app/stores/StoreContext'
import type { LocationJson } from 'app/types/entities'
import { bg } from 'lib/background'

const CHIP_WORDS: Record<AgentStatusTone, string> = {
  online: `Online`,
  redirecting: `Redirecting`,
  interrupted: `Interrupted`,
  offline: `Offline`,
}

/**
 * The agent's live status for the dashboard's locations, by the same rules as the Status card on
 * Settings. A voice location counts only once it has an agent set up: the product alone is not one. One tone across them all reads as that tone. A mix shows a chip per tone with its
 * count, and each chip lists its locations on hover.
 */
export const AgentLiveStatus = observer(function AgentLiveStatus({
  kind,
}: {
  kind: `voice` | `sms`
}): ReactElement | null {
  const locationStore = useLocationStore()
  const [searchParams] = useSearchParams()
  // The literal name matters: bin/generate-feature-toggle-names.mjs reads it to front-load the toggle.
  const globallyEnabled = useFeatureToggle(`ai-agent-enabled`)
  const agentStore = useAIAgentStore()
  const product = kind === `voice` ? `AI Agent` : `SMS Agent`
  const [locations, setLocations] = useState<LocationJson[]>()

  useEffect(() => {
    bg(
      locationStore.search({ pagination: { limit: 500 } }).then(async (result) => {
        const withProduct = result.hits.filter((location) => location.products?.includes(product))
        if (kind === `sms`) return setLocations(withProduct)
        const withAgent = await agentStore.locationIdsWithAgent(withProduct.map((location) => location.id!))
        setLocations(withProduct.filter((location) => withAgent.has(location.id!)))
      }),
    )
  }, [locationStore, agentStore, kind, product])

  if (!locations) return null
  const selected = compact(searchParams.get(`locationIds`)?.split(`,`) ?? [])
  const rows = locations
    .filter((location) => !selected.length || selected.includes(location.id!))
    .map((location) => ({
      name: location.name ?? `Location`,
      line: agentStatusLines({ voiceLive: !!location.isAIAgentLive, smsOn: true, globallyEnabled })[kind],
    }))
  if (rows.length === 0) return null

  const first = rows[0].line.tone
  if (rows.every((row) => row.line.tone === first)) return <AgentStatusBadge tone={first} />

  const tones = (Object.keys(TONE_WORDS) as AgentStatusTone[]).filter((tone) =>
    rows.some((row) => row.line.tone === tone),
  )
  return (
    <span className="flex items-center gap-2">
      {tones.map((tone) => {
        const names = rows.filter((row) => row.line.tone === tone).map((row) => row.name)
        return (
          <TooltipWrapper
            key={tone}
            placement="bottom-end"
            message={
              <span className="flex flex-col gap-1">
                <span className="font-semibold">{TONE_WORDS[tone]}</span>
                {names.map((name) => (
                  <span key={name}>{name}</span>
                ))}
              </span>
            }
          >
            <span className="cursor-help">
              <AgentStatusBadge tone={tone} label={`${names.length} ${CHIP_WORDS[tone]}`} />
            </span>
          </TooltipWrapper>
        )
      })}
    </span>
  )
})
